import { useCallback, useState, useEffect } from 'react';

import _ from 'lodash';
import IconButton from '@mui/material/IconButton';
import { styled, keyframes } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import makeApiRequest from 'src/services/api';
import { useAuthContext } from 'src/auth/hooks';
import { Badge } from '@mui/material';
import { usePendingTasks } from 'src/components/plaid/datasource/usePendingTasks';

// import { useFinanceContext } from 'src/components/plaid/datasource';
// import { Badge } from '@mui/material';

// Define the rotation keyframes
const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Create a styled component with the animation
const RotatingIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'rotate',
})(({ rotate }) => ({
  ...(rotate && {
    animation: `${rotateAnimation} 1s ease-in-out`,
  }),
}));

function RefreshButtonWithRotation({ width = 24, onClick, sx }) {
  const { user } = useAuthContext();
  const { hasPendingTasks } = usePendingTasks(user?.id);

  const [rotate, setRotate] = useState(false);
  const [isPending, setIsPending] = useState(false);
  useEffect(() => setIsPending(hasPendingTasks), [hasPendingTasks]);

  const handleClick = useCallback(() => {
    setIsPending(false);
    setRotate(true);
    onClick(user?.uid);

    // Remove the rotation class after the animation ends
    setTimeout(() => setRotate(false), 1000);
  }, [onClick, user?.uid]);

  const debouncedHandleClick = useCallback(
    _.debounce(() => {
      handleClick();
    }, 1000),
    [handleClick]
  );

  useEffect(() => {
    if (hasPendingTasks) {
      setIsPending(true);
      debouncedHandleClick();
    }
  }, [hasPendingTasks, debouncedHandleClick]);

  return (
    <RotatingIconButton onClick={handleClick} rotate={rotate} sx={sx}>
      <Badge color="error" invisible={!isPending} variant="dot">
        <Iconify icon="material-symbols:refresh" width={width} />
      </Badge>
    </RotatingIconButton>
  );
}

export default function RefreshButton({ width = 26 }) {
  const reloadAccount = (uid) => {
    makeApiRequest(`/api/providers/sync/${uid}`);
  };

  return (
    <RefreshButtonWithRotation
      width={width}
      onClick={reloadAccount}
      sx={{ display: process.env.NODE_ENV !== 'development' ? 'none' : 'block' }}
    />
  );
}
